import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarRange, History, ChevronRight, Trophy } from "lucide-react";
import { fmtINR, fmtNum, fmtDate } from "@/utils/format";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useProducts, productColor } from "@/lib/products";

export function PerFuelChart({ fuel, data, color, title }) {
  const hasData = data.some((d) => d.value > 0);
  return (
    <Card className="p-4 rounded-2xl border-slate-200" data-testid={`fuel-chart-${fuel}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500">By shift · employee</div>
          <div className="font-display font-bold flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} /> {title}
          </div>
        </div>
      </div>
      <div className="h-56">
        {!hasData ? (
          <div className="h-full grid place-items-center text-xs text-slate-400">No data yet today</div>
        ) : (
          <ResponsiveContainer>
            <BarChart data={data} margin={{ left: -10, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#475569" }} interval={0} angle={-25} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
              <Tooltip
                formatter={(v) => [`${fmtNum(v)} L`, title]}
                labelFormatter={(l, p) => {
                  const row = p?.[0]?.payload;
                  return row ? `${row.employee} · ${row.mpd} · Shift ${row.shift}` : l;
                }}
              />
              <Bar dataKey="value" fill={color} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}

export function RecentDaysTable() {
  const products = useProducts();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/daily-reports").then((r) => setData(r.data)); }, []);
  const t = data?.totals;
  return (
    <Card className="p-5 rounded-2xl border-slate-200 mb-8" data-testid="recent-days-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-[color:var(--bpcl-blue)]" />
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Last 30 days</div>
            <div className="font-display font-bold text-lg">Day-wise litres sold · click a day for full report</div>
          </div>
        </div>
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => nav("/admin/daily")} data-testid="open-daily-reports-btn">
          All daily reports <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>
      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <MiniStat label="30-day Sales" value={fmtINR(t.grand_sales)} tone="blue" />
          {products.map((p) => <MiniStat key={p.key} label={p.name} value={`${fmtNum(t[`net_${p.key}`])} ${p.unit}`} />)}
        </div>
      )}
      <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
        <table className="w-full text-sm" data-testid="recent-days-table">
          <thead className="sticky top-0 bg-white">
            <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">Date</th>
              {products.map((p) => <th key={p.key} className="py-2 pr-3 text-right">{p.short} {p.unit}</th>)}
              <th className="py-2 pr-3 text-right">Net L</th>
              <th className="py-2 pr-3 text-right">Sale</th>
              <th className="py-2 pr-3 text-right">Paytm</th>
              <th className="py-2 pr-3 text-right">Credit</th>
              <th className="py-2 pr-3 text-right">Cash</th>
              <th className="py-2 pr-3 text-right">Shifts</th>
            </tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={10} className="py-6 text-center text-slate-400">Loading…</td></tr>}
            {data?.rows?.length === 0 && <tr><td colSpan={10} className="py-6 text-center text-slate-400">No completed shifts in the last 30 days.</td></tr>}
            {data?.rows?.map((r) => (
              <tr
                key={r.business_date}
                data-testid={`recent-day-${r.business_date}`}
                onClick={() => nav(`/admin/daily/${r.business_date}`)}
                className="border-b border-slate-100 hover:bg-[color:var(--bpcl-blue)]/5 cursor-pointer font-mono-num"
              >
                <td className="py-2 pr-3 font-sans font-medium text-[color:var(--bpcl-blue)] whitespace-nowrap">{fmtDate(r.business_date)}</td>
                {products.map((p) => <td key={p.key} className="py-2 pr-3 text-right">{fmtNum(r[`net_${p.key}`])}</td>)}
                <td className="py-2 pr-3 text-right font-semibold">{fmtNum(r.net_total_litres)}</td>
                <td className="py-2 pr-3 text-right font-semibold">{fmtINR(r.grand_sales)}</td>
                <td className="py-2 pr-3 text-right">{fmtINR(r.paytm)}</td>
                <td className="py-2 pr-3 text-right">{fmtINR(r.credit)}</td>
                <td className="py-2 pr-3 text-right text-emerald-700">{fmtINR(r.cash)}</td>
                <td className="py-2 pr-3 text-right">{r.shifts_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function Leaderboard() {
  const products = useProducts();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/leaderboard").then((r) => setD(r.data)); }, []);
  const max = d?.rows?.[0]?.net_total_litres || 1;
  const monthLabel = d ? new Date(d.year, d.month - 1).toLocaleString("en-IN", { month: "long", year: "numeric" }) : "";
  const medal = ["bg-[color:var(--bpcl-orange)] text-white", "bg-slate-300 text-slate-800", "bg-amber-700/80 text-white"];
  return (
    <Card className="p-5 rounded-2xl border-slate-200 mb-8" data-testid="leaderboard-section">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-4 h-4 text-[color:var(--bpcl-orange-dark)]" />
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">Employee leaderboard</div>
          <div className="font-display font-bold text-lg">Top DSMs · {monthLabel}</div>
        </div>
      </div>
      {!d ? <div className="h-32 animate-pulse bg-slate-100 rounded-xl" /> : d.rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400">No completed shifts this month yet.</div>
      ) : (
        <div className="space-y-2">
          {d.rows.map((r) => (
            <div key={r.employee_id} data-testid={`leader-${r.employee_code}`} className="relative overflow-hidden rounded-xl border border-slate-200 p-3">
              <div className="absolute inset-y-0 left-0 bg-[color:var(--bpcl-blue)]/8" style={{ width: `${(r.net_total_litres / max) * 100}%` }} />
              <div className="relative flex items-center gap-3 flex-wrap">
                <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${medal[r.rank - 1] || "bg-slate-100 text-slate-600"}`}>{r.rank}</span>
                <div className="min-w-[140px]">
                  <div className="font-semibold text-sm">{r.employee_name} <span className="text-slate-400 font-normal">({r.employee_code})</span></div>
                  <div className="text-[11px] text-slate-500">{r.shifts_count} shifts</div>
                </div>
                <div className="ml-auto flex gap-4 text-right font-mono-num text-sm flex-wrap">
                  {products.map((p) => <div key={p.key}><div className="text-[10px] uppercase text-slate-500 font-sans">{p.short}</div>{fmtNum(r[`net_${p.key}`])} {p.unit}</div>)}
                  <div className="font-bold text-[color:var(--bpcl-blue)]"><div className="text-[10px] uppercase text-slate-500 font-sans font-normal">Total</div>{fmtNum(r.net_total_litres)} L<div className="text-[11px] text-slate-500 font-normal">{fmtINR(r.grand_sales)}</div></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function AnalyticsSection() {
  const products = useProducts();
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/admin/analytics", { params: { period } }).then((r) => setData(r.data));
  }, [period]);

  return (
    <Card className="p-5 rounded-2xl border-slate-200 mb-8" data-testid="analytics-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <CalendarRange className="w-4 h-4 text-[color:var(--bpcl-blue)]" />
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Analytics</div>
            <div className="font-display font-bold text-lg">
              {period === "monthly"
                ? `Month view · ${data?.year ? new Date(data.year, (data.month || 1) - 1).toLocaleString("en-IN", { month: "long", year: "numeric" }) : ""}`
                : `Year view · ${data?.year || ""}`}
            </div>
          </div>
        </div>
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList data-testid="analytics-tabs">
            <TabsTrigger value="monthly" data-testid="tab-monthly">Monthly</TabsTrigger>
            <TabsTrigger value="yearly" data-testid="tab-yearly">Yearly</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {!data ? (
        <div className="h-64 animate-pulse bg-slate-100 rounded-xl" />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <MiniStat label={`Total Sales`} value={fmtINR(data.totals.sales)} tone="blue" />
            <MiniStat label="Net Litres" value={`${fmtNum(data.totals.net)} L`} />
            <MiniStat label="Testing" value={`${fmtNum(data.totals.testing)} L`} />
            <MiniStat label="Gross Litres" value={`${fmtNum(data.totals.gross)} L`} />
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={data.rows}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#475569" }} interval={0} />
                <YAxis tick={{ fontSize: 12, fill: "#475569" }} />
                <Tooltip formatter={(v, n) => [fmtINR(v), n]} />
                {products.map((p, i) => <Bar key={p.key} dataKey={p.key} stackId="a" fill={productColor(i)} name={p.short} radius={i === products.length - 1 ? [8, 8, 0, 0] : [0, 0, 0, 0]} />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex items-center gap-4 text-xs text-slate-600 flex-wrap">
            {products.map((p, i) => <LegendDot key={p.key} color={productColor(i)} label={`${p.short} ${fmtINR(data.totals[p.key])}`} />)}
          </div>
        </>
      )}
    </Card>
  );
}

export function MiniStat({ label, value, tone }) {
  return (
    <div className={`rounded-xl p-3 border ${tone === "blue" ? "bg-[color:var(--bpcl-blue)]/5 border-[color:var(--bpcl-blue)]/20" : "bg-slate-50 border-slate-200"}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`font-mono-num text-lg font-bold mt-0.5 ${tone === "blue" ? "text-[color:var(--bpcl-blue)]" : ""}`}>{value}</div>
    </div>
  );
}

export function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="w-3 h-3 rounded-full" style={{ background: color }} />
      <span className="font-medium">{label}</span>
    </span>
  );
}
